import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

const HoverPreview = ({ previewUrl, children, style = undefined, disabled = false, previewHeight = 300, title = undefined }) => {
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Becoming disabled mid-hover (e.g. a geotag starts while the cursor is over a
  // thumbnail) must tear the preview down. The panel is a portal with
  // pointerEvents:'none', so once the cursor leaves for the map it never gets a
  // mouseleave — without this the preview hangs over the map until something
  // else forces a re-render.
  useEffect(() => {
    if (!disabled) return;
    if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null; }
    setHoverPos(null);
  }, [disabled]);

  // Clear any pending timer if the component goes away mid-hover.
  useEffect(() => () => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null; }
    if (!ref.current) return;
    // Position preview to the right of the drawer
    const drawer = ref.current.closest('.MuiDrawer-paper');
    const drawerRight = drawer ? drawer.getBoundingClientRect().right : e.clientX + 20;
    // Clamp vertical position to keep preview within viewport
    const halfH = previewHeight / 2;
    const top = Math.max(halfH + 8, Math.min(e.clientY, window.innerHeight - halfH - 8));
    setHoverPos({ x: drawerRight + 8, y: top });
  }, [previewHeight]);

  const handleMouseLeave = useCallback(() => {
    leaveTimer.current = setTimeout(() => setHoverPos(null), 50);
  }, []);

  return (
    <div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={() => setHoverPos(null)}
      style={style || { display: 'contents' }}
    >
      {children}
      {hoverPos && !disabled && createPortal(
        <div style={{
          position: 'fixed',
          left: hoverPos.x,
          top: hoverPos.y,
          transform: 'translateY(-50%)',
          zIndex: 9999,
          pointerEvents: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          borderRadius: '4px',
          overflow: 'hidden',
          backgroundColor: '#222'
        }}>
          <div style={{ position: 'relative' }}>
            <img
              src={previewUrl}
              alt=""
              style={{ display: 'block', height: `${previewHeight}px`, width: 'auto', objectFit: 'contain' }}
            />
            {title && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '4px 8px', color: 'white', fontSize: '12px', backgroundColor: 'rgba(0,0,0,0.55)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', direction: /[\u0590-\u05FF]/.test(title) ? 'rtl' : 'ltr', textAlign: /[\u0590-\u05FF]/.test(title) ? 'right' : 'left' }}>{title}</div>}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default HoverPreview;
